/**
 * Created by neo on 21.01.2026
 * Security Section - Contains security-related settings like email, password, MFA, and account deletion
 */
import * as React from 'react';
import { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { useToast } from '../../Hooks/useToast';
import { useDeleteAthlete } from '../../Hooks/useAthleteMutations';
import { Button } from '../../Components/Button';
import { MfaManagementSection } from './MfaManagementSection';
import { Theme } from '../../Theme/Theme';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { useAuth0 } from '@auth0/auth0-react';

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ActionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ActionInfo = styled.div`
  flex: 1;
`;

const ActionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 6px 0;
`;

const ActionDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const EmailStatusCard = styled(ActionCard)<{ $verified: boolean }>`
  border: 1px solid ${(props) => (props.$verified ? `${Theme.Colors.success}30` : '#fecaca')};
  background: ${(props) => (props.$verified ? `${Theme.Colors.success}08` : '#fffbfb')};
  flex-direction: column;
  gap: 12px;
`;

const EmailStatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const EmailAddress = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: ${Theme.Colors.textColor};
  word-break: break-all;
`;

const VerificationBadge = styled.span<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 16px;
  background: ${(props) => (props.$verified ? `${Theme.Colors.success}15` : '#fef2f2')};
  color: ${(props) => (props.$verified ? Theme.Colors.success : '#dc2626')};
  white-space: nowrap;

  i {
    font-size: 14px;
  }
`;

const UnverifiedWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  font-size: 14px;
  color: #7f1d1d;
  line-height: 1.4;

  i {
    font-size: 18px;
    color: #dc2626;
    flex-shrink: 0;
  }
`;

const ResendButton = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${Theme.Colors.primary};
  border-radius: 8px;
  color: ${Theme.Colors.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${Theme.Colors.primary}10;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SsoInfoCard = styled.div`
  background: linear-gradient(135deg, #eff6ff 0%, #f0f7ff 100%);
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const SsoInfoContent = styled.div`
  flex: 1;
`;

const SsoInfoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 6px 0;
`;

const SsoInfoText = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0;
  line-height: 1.6;
`;

const DangerAction = styled(ActionCard)`
  border: 1px solid #fee2e2;
  background: #fef2f2;
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

const DeleteModal = styled(Modal)`
  .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
`;

const DeleteModalHeader = styled(ModalHeader)`
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  color: white;
  border-radius: 16px 16px 0 0;
  border: none;
  padding: 20px 24px;

  .btn-close {
    filter: brightness(0) invert(1);
  }

  h5 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
  }
`;

const DeleteModalBody = styled(ModalBody)`
  padding: 24px;
`;

const WarningBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const WarningTitle = styled.div`
  font-weight: 700;
  color: #dc2626;
  font-size: 20px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #7f1d1d;
  font-size: 14px;
  line-height: 1.6;
`;

const ConfirmSection = styled.div`
  margin-top: 20px;
`;

const ConfirmLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 8px;
`;

const ConfirmHint = styled.span`
  font-weight: 400;
  color: ${Theme.Colors.charcoal};
`;

const ConfirmInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${(props) => (props.$hasError ? '#dc2626' : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-align: center;
  transition: all 0.2s;
  animation: ${(props) => (props.$hasError ? shake : 'none')} 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? '#dc2626' : '#dc2626')};
    box-shadow: 0 0 0 3px ${(props) => (props.$hasError ? 'rgba(220, 38, 38, 0.2)' : 'rgba(220, 38, 38, 0.1)')};
  }

  &::placeholder {
    color: ${Theme.Colors.warmGrey};
    letter-spacing: 4px;
  }
`;

const DeleteModalFooter = styled(ModalFooter)`
  border: none;
  padding: 16px 24px 24px;
  gap: 12px;
`;

const DeleteButton = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  padding: 14px 24px;
  background: ${(props) => (props.$disabled ? '#fca5a5' : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)')};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.4);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background: white;
  color: ${Theme.Colors.textColor};
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }
`;

export type SecuritySectionProps = {
  canChangeEmail: boolean;
  canChangePassword: boolean;
  canManageMfa: boolean;
  connectionType: string | null;
  connection?: string;
  email?: string;
  emailVerified?: boolean;
  onChangeEmail: () => void;
  onChangePassword: () => void;
  onSetPassword: () => void;
};

const getProviderDisplayName = (connection?: string): string => {
  if (!connection) return 'SSO';
  const map: Record<string, string> = {
    'google-oauth2': 'Google',
    'apple': 'Apple',
    'facebook': 'Facebook',
    'github': 'GitHub',
    'linkedin': 'LinkedIn',
    'twitter': 'Twitter',
    'microsoft': 'Microsoft',
    'windowslive': 'Microsoft',
    'waad': 'Microsoft Azure AD',
    'samlp': 'your organization (SAML)',
    'oidc': 'your organization (OIDC)',
    'okta': 'Okta',
    'auth0': 'Auth0',
  };
  return map[connection] || connection;
};

export const SecuritySection: React.FC<SecuritySectionProps> = observer(
  ({
    canChangeEmail,
    canChangePassword,
    canManageMfa,
    connectionType,
    connection,
    email,
    emailVerified,
    onChangeEmail,
    onChangePassword,
    onSetPassword,
  }) => {
    const { t } = useTrans();
    const { authentication } = useRootStore();
    const toast = useToast();
    const { logout } = useAuth0();

    // Resend verification email state
    const [resending, setResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const handleResendVerification = useCallback(async () => {
      if (resending || resendCooldown > 0) return;

      setResending(true);
      try {
        await HttpBackend.post('/uaa/profile/resend-verification-email', {
          callbackUrl: `${window.location.origin}/auth/callback`,
        });
        toast.showToast(
          t('profile.email.verificationSent', { defaultValue: 'Verification email sent! Please check your inbox.' }),
          'success',
        );
        // Set 60-second cooldown
        setResendCooldown(60);
        const interval = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (err) {
        console.error('Error resending verification email:', err);
        toast.showToast(
          t('profile.email.verificationError', { defaultValue: 'Failed to send verification email. Please try again.' }),
          'error',
        );
      } finally {
        setResending(false);
      }
    }, [resending, resendCooldown, toast, t]);

    // Delete account modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteError, setDeleteError] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const CONFIRM_TEXT = 'DELETE';
    const isConfirmValid = deleteConfirmText.toUpperCase() === CONFIRM_TEXT;

    const handleOpenDeleteModal = useCallback(() => {
      setDeleteModalOpen(true);
      setDeleteConfirmText('');
      setDeleteError(false);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
      if (!isDeleting) {
        setDeleteModalOpen(false);
        setDeleteConfirmText('');
        setDeleteError(false);
      }
    }, [isDeleting]);

    const handleConfirmTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setDeleteConfirmText(e.target.value.toUpperCase());
      setDeleteError(false);
    }, []);

    const deleteAthlete = useDeleteAthlete();

    const handleDeleteAccount = useCallback(async () => {
      if (!isConfirmValid) {
        setDeleteError(true);
        return;
      }

      if (!authentication.athlete) return;

      setIsDeleting(true);

      try {
        await deleteAthlete.mutateAsync();
        
        toast.showToast(
          t('accountScreen.deleteAccount.success', { defaultValue: 'Your account has been deleted. Goodbye!' }),
          'success',
        );

        // Logout after successful deletion
        setTimeout(() => {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }, 1500);
      } catch (error) {
        console.error('Error deleting account:', error);
        toast.showToast(
          t('accountScreen.deleteAccount.error', { defaultValue: 'Failed to delete account. Please try again.' }),
          'error',
        );
        setIsDeleting(false);
      }
    }, [isConfirmValid, authentication.athlete, t, toast, logout, deleteAthlete]);

    const isSso = connectionType === 'sso';
    const providerName = getProviderDisplayName(connection);

    return (
      <SectionContent>
        {isSso && (
          <SsoInfoCard>
            <SsoInfoContent>
              <SsoInfoTitle>
                {t('profile.sso.title', {
                  defaultValue: 'Signed in via {{provider}}',
                  provider: providerName,
                })}
              </SsoInfoTitle>
              <SsoInfoText>
                {t('profile.sso.description', {
                  defaultValue:
                    'Your email, password, and security settings are managed by {{provider}}. To update these, please go to your {{provider}} account settings or contact your IT administrator.',
                  provider: providerName,
                })}
              </SsoInfoText>
            </SsoInfoContent>
          </SsoInfoCard>
        )}

        {email && (
          <EmailStatusCard $verified={!!emailVerified}>
            <EmailStatusRow>
              <ActionInfo>
                <ActionTitle>{t('profile.settings.email', { defaultValue: 'Email Address' })}</ActionTitle>
                <EmailAddress>{email}</EmailAddress>
              </ActionInfo>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <VerificationBadge $verified={!!emailVerified}>
                  <i className={emailVerified ? 'bi bi-check-circle-fill' : 'bi bi-exclamation-circle-fill'} />
                  {emailVerified
                    ? t('profile.email.verified', { defaultValue: 'Verified' })
                    : t('profile.email.unverified', { defaultValue: 'Not verified' })}
                </VerificationBadge>
                {canChangeEmail && (
                  <Button color="secondary" size="sm" onClick={onChangeEmail}>
                    {t('globals.change', { defaultValue: 'Change' })}
                  </Button>
                )}
              </div>
            </EmailStatusRow>
            {!emailVerified && (
              <UnverifiedWarning>
                <i className="bi bi-envelope-exclamation" />
                <div>
                  {t('profile.email.unverifiedWarning', {
                    defaultValue: 'Your email is not verified. Please check your inbox for a verification link, or resend it below.',
                  })}
                </div>
                <ResendButton
                  onClick={handleResendVerification}
                  disabled={resending || resendCooldown > 0}
                >
                  {resending ? (
                    <Spinner size="sm" />
                  ) : resendCooldown > 0 ? (
                    t('profile.email.resendIn', { defaultValue: 'Resend in {{seconds}}s', seconds: resendCooldown })
                  ) : (
                    <>
                      <i className="bi bi-envelope" />
                      {t('profile.email.resend', { defaultValue: 'Resend' })}
                    </>
                  )}
                </ResendButton>
              </UnverifiedWarning>
            )}
          </EmailStatusCard>
        )}

        {connectionType === 'passwordless-email' ? (
          <ActionCard>
            <ActionInfo>
              <ActionTitle>{t('profile.settings.setPassword', { defaultValue: 'Set Password' })}</ActionTitle>
              <ActionDescription>
                {t('profile.password.setInfo', { defaultValue: 'Enable email & password login' })}
              </ActionDescription>
            </ActionInfo>
            <Button color="secondary" size="sm" onClick={onSetPassword}>
              {t('globals.set', { defaultValue: 'Set' })}
            </Button>
          </ActionCard>
        ) : canChangePassword ? (
          <ActionCard>
            <ActionInfo>
              <ActionTitle>{t('profile.settings.changePassword', { defaultValue: 'Change Password' })}</ActionTitle>
              <ActionDescription>
                {t('profile.password.changeDescription', { defaultValue: 'Update your account password' })}
              </ActionDescription>
            </ActionInfo>
            <Button color="secondary" size="sm" onClick={onChangePassword}>
              {t('globals.change', { defaultValue: 'Change' })}
            </Button>
          </ActionCard>
        ) : null}

        {canManageMfa && <MfaManagementSection />}

        <DangerAction>
          <ActionInfo>
            <ActionTitle style={{ color: '#dc2626' }}>
              {t('accountScreen.deleteAccount.title', { defaultValue: 'Delete Account' })}
            </ActionTitle>
            <ActionDescription>
              {t('accountScreen.deleteAccount.description', {
                defaultValue: 'Permanently delete your account and all associated data. This action cannot be undone.',
              })}
            </ActionDescription>
          </ActionInfo>
          <Button color="danger" size="sm" onClick={handleOpenDeleteModal}>
            {t('accountScreen.deleteAccountButton', { defaultValue: 'Delete Account' })}
          </Button>
        </DangerAction>

        {/* Delete Account Confirmation Modal */}
        <DeleteModal isOpen={deleteModalOpen} toggle={handleCloseDeleteModal} centered>
          <DeleteModalHeader toggle={handleCloseDeleteModal}>
            <i className="bi bi-exclamation-triangle-fill" />
            {t('accountScreen.deleteAccount.modal.title', { defaultValue: 'Delete Your Account' })}
          </DeleteModalHeader>
          <DeleteModalBody>
            <WarningBox>
              <WarningTitle>
                <i className="bi bi-shield-exclamation" />
                {t('accountScreen.deleteAccount.modal.warningTitle', { defaultValue: 'This action is permanent and cannot be undone!' })}
              </WarningTitle>
              <WarningList>
                <li>{t('accountScreen.deleteAccount.modal.warning1', { defaultValue: 'All your personal data will be permanently deleted' })}</li>
                <li>{t('accountScreen.deleteAccount.modal.warning2', { defaultValue: 'Your workout history and progress will be lost' })}</li>
                <li>{t('accountScreen.deleteAccount.modal.warning3', { defaultValue: 'Any active subscriptions will be cancelled' })}</li>
                <li>{t('accountScreen.deleteAccount.modal.warning4', { defaultValue: 'You will not be able to recover your account' })}</li>
              </WarningList>
            </WarningBox>

            <ConfirmSection>
              <ConfirmLabel>
                {t('accountScreen.deleteAccount.modal.confirmLabel', { defaultValue: 'To confirm, type' })}{' '}
                <strong style={{ color: '#dc2626' }}>DELETE</strong>{' '}
                <ConfirmHint>{t('accountScreen.deleteAccount.modal.confirmHint', { defaultValue: 'below:' })}</ConfirmHint>
              </ConfirmLabel>
              <ConfirmInput
                type="text"
                value={deleteConfirmText}
                onChange={handleConfirmTextChange}
                placeholder="DELETE"
                $hasError={deleteError}
                disabled={isDeleting}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </ConfirmSection>
          </DeleteModalBody>
          <DeleteModalFooter>
            <CancelButton onClick={handleCloseDeleteModal} disabled={isDeleting}>
              {t('globals.cancel', { defaultValue: 'Cancel' })}
            </CancelButton>
            <DeleteButton
              onClick={handleDeleteAccount}
              $disabled={!isConfirmValid || isDeleting}
              disabled={!isConfirmValid || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Spinner size="sm" />
                  {t('accountScreen.deleteAccount.modal.deleting', { defaultValue: 'Deleting...' })}
                </>
              ) : (
                <>
                  <i className="bi bi-trash3-fill" />
                  {t('accountScreen.deleteAccount.modal.confirmButton', { defaultValue: 'Permanently Delete' })}
                </>
              )}
            </DeleteButton>
          </DeleteModalFooter>
        </DeleteModal>
      </SectionContent>
    );
  },
);
